import React, { useState } from 'react';

import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { useGetFreezeStorageLocationsQuery } from '@/app/services/locations';
import { addToast } from '@/app/slices/toast';
import {
  AccountAutoComplete,
  OptionSetsDropdown,
  StorageLocationAutoComplete,
} from '@/components/common';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { AccountAutoCompleteItem } from '@/services/lookups/types';

import {
  useUpdateFreezeEmbryosAsync,
  useUpdateFreezeTempEmbryosAsync,
} from '../../../services/freeze/hooks';
import { SelectionService } from '../utils/selectionService';
import { getFertilizationSiteId } from '../utils/utils';

export const FreezeEditBar = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const siteId = params.get('sId');

  //const [name, setName] = React.useState<string>(); // TODO: add back in if we want to allow changing mating name
  const [freezer, setFreezer] = React.useState<string>();
  const [storageLocation, setStorageLocation] = React.useState();
  const [outsideFreeze, setOutsideFreeze] = useState(false);
  const isEditViewOutsideFreezeEnabled = useFeatureFlag(
    'Edit_View_Outside_Freeze',
  );
  const [freezeMethod, setFreezeMethod] = React.useState();
  const [grader, setGrader] = React.useState();
  const [freezeTech, setFreezeTech] = React.useState();
  const [account, setAccount] = React.useState<AccountAutoCompleteItem | null>(
    null,
  );
  const [deactivate, setDeactivate] = React.useState(false);
  const [crackedZona, setCrackedZona] = React.useState(false);
  const [lowQuality, setLowQuality] = React.useState(false);

  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });

  const mappedTechs =
    techsData?.map((tech) => ({
      label: tech.name,
      value: tech.id,
    })) || [];

  const { data: storageLocations } = useGetFreezeStorageLocationsQuery(
    getFertilizationSiteId(siteId),
  );
  const { mutateAsync: updateEmbryosAsync } = useUpdateFreezeEmbryosAsync();
  const { mutateAsync: updateTempEmbryosAsync } =
    useUpdateFreezeTempEmbryosAsync();
  const handleUpdateEmbryosAsync = () => {
    const selection = SelectionService.getSelection();
    const { liveEmbryoIds, tempEmbryoIds } = selection;

    if (!liveEmbryoIds.length && !tempEmbryoIds.length) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('embryo.noEmbryosOrTempEmbryosSelected'),
        }),
      );
      return;
    }

    if (liveEmbryoIds.length) {
      updateEmbryosAsync({
        embryoIds: liveEmbryoIds,
        name: null, //name      TODO: add back in if we want to allow changing mating name
        storageLocationId: storageLocation,
        freezeMethodId: freezeMethod,
        graderId: grader,
        freezeTechId: freezeTech,
        accountId: account?.id,
        deactivate,
        crackedZona,
        lowQuality,
        freezer,
      });
    }
    if (tempEmbryoIds.length) {
      updateTempEmbryosAsync({
        tempEmbryoIds: tempEmbryoIds,
        name: null, //name      TODO: add back in if we want to allow changing mating name
        storageLocationId: storageLocation,
        freezeMethodId: freezeMethod,
        freezeTechId: freezeTech,
        accountId: account?.id,
        deactivate,
        crackedZona,
        lowQuality,
        freezer,
      });
    }
  };

  return (
    <div className="freeze-toolbar__row">
      {/* <div className="label-input-group"> */}
      {/* <label>Mating Name</label> TODO: add back in if we want to allow changing mating name */}
      {/* <InputText
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Change Mating Name"
          className="p-inputtext p-component"
          </div>
          /> */}
      <div className="label-input-group">
        <label>{t('freeze.freezer')}</label>
        <InputText
          type="text"
          value={freezer}
          onChange={(e) => setFreezer(e.target.value)}
          placeholder={t('freeze.changeFreezer')}
          className="p-inputtext p-component"
        />
      </div>
      {isEditViewOutsideFreezeEnabled && (
        <div className="label-input-group">
          <label>{t('freeze.outsideFreeze')}</label>
          <Checkbox
            checked={outsideFreeze}
            onChange={(e) => {
              setOutsideFreeze(e.checked as boolean);
              setStorageLocation(undefined);
            }}
          />
        </div>
      )}
      <div className="label-input-group">
        {isEditViewOutsideFreezeEnabled && outsideFreeze ? (
          <StorageLocationAutoComplete
            label={t('storageLocation.storageLocation')}
            value={storageLocation}
            field="name"
            onSelect={(e: AutoCompleteSelectEvent) =>
              setStorageLocation(e.value.id)
            }
            appendTo={null}
          />
        ) : (
          <>
            <label>{t('storageLocation.storageLocation')}</label>
            <Dropdown
              options={[
                {
                  label: t('storageLocation.selectStorageLocation'),
                  value: '0',
                },
                ...(storageLocations || []).map((location) => ({
                  label: location.name,
                  value: location.id,
                })),
              ]}
              value={storageLocation}
              onChange={(e) => {
                setStorageLocation(e.value);
              }}
              placeholder={t('storageLocation.selectStorageLocation')}
              scrollHeight="34rem"
              panelStyle={{ height: '40rem' }}
              virtualScrollerOptions={{
                itemSize: 25,
              }}
              filter
              filterBy="label"
            />
          </>
        )}
      </div>
      <div className="label-input-group">
        <OptionSetsDropdown
          label={t('freeze.freezeMethod')}
          appendTo={null}
          value={freezeMethod}
          placeholder={t('freeze.selectFreezeMethod')}
          optionSetsName="Freezing Methods"
          onChange={(e: DropdownChangeEvent) => setFreezeMethod(e.value)}
        />
      </div>
      <div className="label-input-group">
        <label>{t('grading.grader')}</label>
        <Dropdown
          options={mappedTechs}
          value={grader}
          onChange={(e) => setGrader(e.value)}
          placeholder={t('grading.selectGrader')}
          className="freeze-tech"
          virtualScrollerOptions={{
            itemSize: 25,
          }}
          filter
        />
      </div>
      <div className="label-input-group">
        <label>{t('freeze.freezeTech')}</label>
        <Dropdown
          options={mappedTechs}
          value={freezeTech}
          onChange={(e) => setFreezeTech(e.value)}
          placeholder={t('freeze.selectFreezeTech')}
          className="freeze-tech"
          virtualScrollerOptions={{
            itemSize: 25,
          }}
          filter
        />
      </div>
      <div className="label-input-group">
        <label>{t('account.account')}</label>
        <AccountAutoComplete
          appendTo={null}
          value={freezeTech}
          onSelect={(e: AutoCompleteSelectEvent) => {
            setAccount(e.value as AccountAutoCompleteItem);
          }}
          placeholder={t('account.selectAccount')}
          className="freeze-tech"
        />
      </div>
      <div className="label-input-group">
        <label>{t('common.deactivate')}</label>
        <Checkbox
          type="checkbox"
          checked={deactivate}
          onChange={(e) => setDeactivate(e.target.checked as boolean)}
        />
      </div>
      <div className="label-input-group">
        <label>{t('embryo.crackedZona')}</label>
        <Checkbox
          type="checkbox"
          checked={crackedZona}
          onChange={(e) => setCrackedZona(e.target.checked as boolean)}
        />
      </div>
      <div className="label-input-group">
        <label>{t('grading.lowQuality')}</label>
        <Checkbox
          type="checkbox"
          checked={lowQuality}
          onChange={(e) => setLowQuality(e.target.checked as boolean)}
        />
      </div>

      <div className="label-input-group">
        <label htmlFor="save-button">&nbsp;</label>
        <Button
          label={t('embryo.updateEmbryos')}
          onClick={handleUpdateEmbryosAsync}
        />
      </div>
    </div>
  );
};
